import * as React from 'react'

import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  createTheme,
  useTheme,
} from '@mui/material'

import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../_helpers/hooks'
import { IUserData } from '../../_models/userInterface'

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PERMISSIONS } from '../../_constants'
import { getUserTypeFromToken } from '../../_helpers/cookie'
import { IBusinessProfile } from '../../_models/businessInterface'
import { LogoutBoxRLineIcon } from '../../components/icons'
import { useUserPermissionAuthorization } from '../../hooks'
import { logout } from '../../slices/authenticationSlice'
import { businessData, switchProfile } from '../../slices/businessSlice'
import { closeDrawer } from '../../slices/newBookingSlice'
import {
  resetProfileDrawerClose,
  superAdminBusinessState,
  superAdminSwitchProfile,
} from '../../slices/superAdmin/businessSlice'
import { userState } from '../../slices/userSlice'
import ListBusinessProfiles from './listBusinessProfiles'

interface IPopper {
  userData: IUserData | Record<string, never>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  popoverPlacement: any
  handleDrawerToggle: () => void
  handleDrawerOnPopoverClick: () => void
  superAdmin?: boolean
  onDrawerClose?: () => void
  children?: {
    businessHeader?: React.ReactNode
    businessList?: React.ReactNode
    menuItems?: (onClose: () => void) => React.ReactNode
    superAdminText?: (onClose: () => void) => React.ReactNode
  }
}
export default function ProfileDrawer({
  userData,
  popoverPlacement,
  handleDrawerOnPopoverClick,
  superAdmin,
  onDrawerClose,
  children,
}: IPopper) {
  const businessDetails = useAppSelector(businessData)
  const userDetails = useAppSelector(userState)
  // eslint-disable-next-line camelcase
  const { userProfileInformation, active_business_id } = userDetails
  const { allocatedBusinessList } = businessDetails
  const superAdminBusinessDetails = useAppSelector(superAdminBusinessState)
  const { closeProfileDrawer } = superAdminBusinessDetails
  const [isPermitted] = useUserPermissionAuthorization()
  const { t } = useTranslation()
  // eslint-disable-next-line camelcase
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const themes = useTheme()

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const open = Boolean(anchorEl)

  const id = open ? 'simple-popper' : undefined

  const loggedInUserType = getUserTypeFromToken()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleSwitchProfile = (
    profileType: string,
    profileData?: IBusinessProfile | { id: number },
  ) => {
    let payload
    if (profileType === 'business' && profileData) {
      payload = {
        type: profileType,
        // eslint-disable-next-line camelcase
        business_id: profileData.id,
      }
    } else {
      payload = {
        type: profileType,
      }
    }
    if (loggedInUserType === 'super_admin') {
      dispatch(superAdminSwitchProfile(payload))
    } else {
      dispatch(switchProfile(payload))
    }
    handleClose()
  }

  const handleClose = React.useCallback(() => {
    document.body.classList.remove('no-scroll')
    setAnchorEl(null)
    dispatch(closeDrawer())
    if (onDrawerClose) {
      onDrawerClose()
    }
  }, [dispatch, onDrawerClose])

  useEffect(() => {
    if (closeProfileDrawer) {
      handleClose()
      dispatch(resetProfileDrawerClose())
    }
  }, [closeProfileDrawer, dispatch, handleClose])

  const handleLogout = () => {
    dispatch(logout())
  }
  const isSuperAdminSwitchedToBusiness =
    // eslint-disable-next-line camelcase
    loggedInUserType === 'super_admin' && active_business_id !== null

  const handleSettingsClick = () => {
    if (loggedInUserType && loggedInUserType === 'customer') {
      navigate('/customer/profile-settings')
    }
    if ((loggedInUserType && loggedInUserType === 'business') || isSuperAdminSwitchedToBusiness) {
      if (
        isPermitted([
          PERMISSIONS.VIEW_BUSINESS_SETTINGS,
          PERMISSIONS.BUSINESS_RESTRICTION_ACCESS,
          PERMISSIONS.SUBSCRIPTION_VIEW,
        ])
      ) {
        navigate('/admin/settings/business-settings')
      } else {
        navigate('/admin/settings/profile-settings')
      }
    }
    handleClose()
    handleDrawerOnPopoverClick()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 1024,
        lg: 1200,
        xl: 1520,
      },
    },
  })
  const showSettings =
    loggedInUserType === 'business' ||
    isSuperAdminSwitchedToBusiness ||
    (loggedInUserType === 'customer' && isPermitted([PERMISSIONS.CUSTOMER_PROFILE_VIEW]))

  return (
    <>
      <Button onClick={handleClick}>
        <Avatar
          alt='Profile Pic'
          src={userData?.image}
          aria-describedby={id}
          imgProps={{ sx: { objectFit: 'contain' } }}
        />
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: popoverPlacement,
        }}
        onClose={handleClose}
        disablePortal
        PaperProps={{
          sx: {
            minWidth: 350,
            maxWidth: { xs: 280, sm: 500 },
            maxHeight: '41.5rem',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {Object.keys(children || {}).length === 0 && (
          <Box display={'flex'} p={2} mr={2} sx={{ flexShrink: 0 }}>
            <Avatar src={userProfileInformation?.image} alt={userProfileInformation?.name} />
            <Box pt={0.5} overflow={'hidden'}>
              <Tooltip title={userProfileInformation?.name} placement='bottom-start'>
                <Typography
                  variant='subtitle1'
                  color='common.black'
                  fontWeight={'normal'}
                  ml={2}
                  lineHeight={1}
                  sx={{
                    [theme.breakpoints.up('md')]: {
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      maxWidth: '10rem',
                    },
                  }}
                >
                  {userProfileInformation?.name}
                </Typography>
              </Tooltip>
              <Tooltip title={userProfileInformation?.email} placement='bottom-start'>
                <Typography
                  variant='subtitle2'
                  color='description.light'
                  fontWeight={300}
                  ml={2}
                  dir='ltr'
                  sx={{
                    [theme.breakpoints.up('md')]: {
                      textOverflow: 'ellipsis',
                      textAlign: 'left',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      maxWidth: '10rem',
                    },
                  }}
                >
                  {userProfileInformation?.email}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        )}
        <Box sx={{ flexShrink: 0, mt: 1, mb: 1 }}>
          <Box>
              { children?.superAdminText?.(() => {
                handleClose()
                handleDrawerOnPopoverClick()
              })}
          </Box>
        </Box>
        <Divider sx={{ mb: 1, flexShrink: 0 }} />
        {children?.businessHeader}
        <Box
          id='scrollableDiv'
          sx={{
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '0.375rem',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '0.188rem',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '0.188',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}
        >
          {children?.businessList || (
            <ListBusinessProfiles
              profiles={allocatedBusinessList}
              switchProfile={handleSwitchProfile}
              superAdmin={superAdmin}
            />
          )}
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <Divider sx={{ mt: 1 }} />
          <List>
            {children?.menuItems?.(() => {
                    handleClose()
                    handleDrawerOnPopoverClick()
            }) || (showSettings && (
                  <>
                    <ListItem disablePadding onClick={handleSettingsClick}>
                      <ListItemButton>
                        <ListItemIcon sx={{ mr: 2 }}>
                          <SettingsOutlinedIcon sx={{ width: 20, height: 20 }} />
                        </ListItemIcon>
                        <ListItemText>
                          <Typography
                            variant='subtitle1'
                            color='description.light'
                            fontWeight={'normal'}
                          >
                            {t('superAdminProfileDrawer.settingsMenu')}
                          </Typography>
                        </ListItemText>
                      </ListItemButton>
                    </ListItem>
                  </>
                ))
            }
            <ListItem
              disablePadding
              onClick={() => {
                handleLogout()
              }}
            >
              <ListItemButton>
                <ListItemIcon sx={{ mr: 2 }}>
                  <Box sx={{ transform: themes.direction === 'rtl' ? 'scaleX(-1)' : 'scaleX(1)' }}>
                    <LogoutBoxRLineIcon
                      width={'1.25rem'}
                      height={'1.25rem'}
                      fill={themes.palette.toggleButtonGroup.contrastText}
                    />
                  </Box>
                </ListItemIcon>
                <ListItemText>
                  <Typography variant='subtitle1' color='description.light' fontWeight={'normal'}>
                    {t('superAdminProfileDrawer.signOutText')}
                  </Typography>
                </ListItemText>
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Popover>
    </>
  )
}
